import React, { useEffect, useRef, useState } from "react";
import Button from "./Button";
import { uploadImage } from "../apiComponents/api-image";

const ProductEditModal = ({ product, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: "",
    price: "",
    location: "",
    image1: "",
    image2: "",
    image3: "",
    image4: "",
    image5: "",
    description: "",
    contact_phone: "",
    contact_whatsapp: "",
    is_available: true,
  });

  const [newImageFiles, setNewImageFiles] = useState({
    image1: null,
    image2: null,
    image3: null,
    image4: null,
    image5: null,
  });

  const [previews, setPreviews] = useState({
    image1: "",
    image2: "",
    image3: "",
    image4: "",
    image5: "",
  });

  const [saving, setSaving] = useState(false);

  const fileInputRefs = {
    image1: useRef(null),
    image2: useRef(null),
    image3: useRef(null),
    image4: useRef(null),
    image5: useRef(null),
  };

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || "",
        price: product.price || "",
        location: product.location || "Swellendam",

        image1: product.image1 || "",
        image2: product.image2 || "",
        image3: product.image3 || "",
        image4: product.image4 || "",
        image5: product.image5 || "",

        description: product.description || "",
        contact_phone: product.contact_phone || "",
        contact_whatsapp: product.contact_whatsapp || "",

        is_available:
          product.is_available !== undefined
            ? product.is_available
            : true,
      });

      setPreviews({
        image1: product.image1 || "",
        image2: product.image2 || "",
        image3: product.image3 || "",
        image4: product.image4 || "",
        image5: product.image5 || "",
      });

      setNewImageFiles({
        image1: null,
        image2: null,
        image3: null,
        image4: null,
        image5: null,
      });
    }
  }, [product]);

  if (!product) return null;

  const imageFields = [
    {
      name: "image1",
      label: "Image 1",
      required: true,
    },
    {
      name: "image2",
      label: "Image 2",
      required: true,
    },
    {
      name: "image3",
      label: "Image 3",
      required: false,
    },
    {
      name: "image4",
      label: "Image 4",
      required: false,
    },
    {
      name: "image5",
      label: "Image 5",
      required: false,
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvailabilityChange = (e) => {
    setForm((prev) => ({
      ...prev,
      is_available: e.target.value === "true",
    }));
  };

  const handleFileChange = (e, imageField) => {
    const file = e.target.files[0];

    if (!file) return;

    // Store the new file
    setNewImageFiles((prev) => ({
      ...prev,
      [imageField]: file,
    }));

    // Show immediate preview
    setPreviews((prev) => ({
      ...prev,
      [imageField]: URL.createObjectURL(file),
    }));
  };

  const handleSubmit = async () => {
    // Image 1 and Image 2 are required
    if (!form.image1 && !newImageFiles.image1) {
      alert("Image 1 is required.");
      return;
    }

    if (!form.image2 && !newImageFiles.image2) {
      alert("Image 2 is required.");
      return;
    }

    try {
      setSaving(true);

      const finalImages = {
        image1: form.image1,
        image2: form.image2,
        image3: form.image3 || null,
        image4: form.image4 || null,
        image5: form.image5 || null,
      };

      // Upload only images that were replaced
      for (const imageField of [
        "image1",
        "image2",
        "image3",
        "image4",
        "image5",
      ]) {
        const file = newImageFiles[imageField];

        if (file) {
          console.log(`Uploading ${imageField}:`, file.name);

          const uploaded = await uploadImage(file);

          console.log(`${imageField} uploaded:`, uploaded);

          finalImages[imageField] =
            typeof uploaded === "string"
              ? uploaded
              : uploaded?.url ||
                uploaded?.image ||
                "";
        }
      }

      // Make sure required images still exist
      if (!finalImages.image1 || !finalImages.image2) {
        alert("Image 1 and Image 2 are required.");
        return;
      }

      console.log("FINAL IMAGES:", finalImages);

      await onSave(product.id, {
        name: form.name,
        price: form.price,
        location: form.location,

        image1: finalImages.image1,
        image2: finalImages.image2,
        image3: finalImages.image3,
        image4: finalImages.image4,
        image5: finalImages.image5,

        description: form.description,
        contact_phone: form.contact_phone,
        contact_whatsapp: form.contact_whatsapp,

        is_available: form.is_available,
      });

    } catch (error) {
      console.error("Error saving product:", error);
      alert("Something went wrong while saving the product.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">

      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg p-6 shadow-xl">

        <h2 className="text-xl font-bold mb-5">
          Edit Product
        </h2>

        <div className="space-y-5">

          {/* IMAGES */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3">
              Product Images
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

              {imageFields.map((image) => (
                <div
                  key={image.name}
                  className="border rounded-lg p-3 bg-gray-50"
                >

                  {/* IMAGE PREVIEW */}
                  <div className="w-full aspect-square rounded-md overflow-hidden bg-gray-200 mb-3">

                    {previews[image.name] ? (
                      <img
                        src={previews[image.name]}
                        alt={image.label}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm text-center">
                        No Image
                      </div>
                    )}

                  </div>

                  {/* LABEL */}
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    {image.label}

                    {image.required && (
                      <span className="text-red-600"> *</span>
                    )}
                  </p>

                  {/* FILE INPUT */}
                  <input
                    type="file"
                    ref={fileInputRefs[image.name]}
                    accept="image/*"
                    className="hidden"
                    onChange={(e) =>
                      handleFileChange(e, image.name)
                    }
                  />

                  {/* CHANGE BUTTON */}
                  <Button
                    onClick={() =>
                      fileInputRefs[image.name].current.click()
                    }
                    variant="outline"
                    className="w-full text-xs"
                  >
                    {previews[image.name]
                      ? "Change Image"
                      : "Select Image"}
                  </Button>

                </div>
              ))}

            </div>
          </div>

          {/* AVAILABILITY */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Availability
            </label>

            <select
              value={form.is_available ? "true" : "false"}
              onChange={handleAvailabilityChange}
              className="w-full border p-2 rounded"
            >
              <option value="true">
                Available
              </option>

              <option value="false">
                Sold
              </option>
            </select>
          </div>

          {/* NAME */}
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            placeholder="Product name"
          />

          {/* PRICE */}
          <input
            name="price"
            value={form.price}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            placeholder="Price"
            type="number"
          />

          {/* LOCATION */}
          <input
            name="location"
            value={form.location}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            placeholder="Location"
          />

          {/* DESCRIPTION */}
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            placeholder="Description"
            rows="4"
          />

          {/* PHONE NUMBER */}
          <input
            name="contact_phone"
            value={form.contact_phone}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            placeholder="Phone Number"
            type="tel"
          />

          {/* WHATSAPP NUMBER */}
          <input
            name="contact_whatsapp"
            value={form.contact_whatsapp}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            placeholder="WhatsApp Number"
            type="tel"
          />

        </div>

        {/* ACTIONS */}
        <div className="flex justify-end gap-2 mt-6">

          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>

        </div>

      </div>

    </div>
  );
};

export default ProductEditModal;
